/**
 * Privacy & Visibility (mobile parity — Aug 2026).
 * Groups: Profile visibility · Activity signals · Location signals · Messaging.
 *   • pause_profile      — global switch (excludes from Discover/Likes/search)
 *   • show_in_discover   — Discover-only (Likes stays)
 *   • online_status_visible — bidirectional hiding of last_active_at
 *   • distance_visible   — blanks distance_km/mi in peer views
 *   • read_receipts_enabled — mirrors legacy `send_read_receipts`
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { ArrowLeft, EyeOff, Compass, Clock, MapPin, MessageSquareText, Loader2 } from 'lucide-react';

const ROWS = [
  { key: 'pause_profile', title: 'Pause my profile', desc: "Hide from Discover, Likes, and search everywhere. Existing matches keep talking.", icon: EyeOff, invert: true },
  { key: 'show_in_discover', title: 'Show me in Discover', desc: 'Turn off to still receive Likes but disappear from the swipe feed.', icon: Compass, invert: false },
  { key: 'online_status_visible', title: 'Show my online status', desc: 'When off, both sides hide last-seen bidirectionally.', icon: Clock, invert: false },
  { key: 'distance_visible', title: 'Show my distance', desc: "Others won't see how far away you are. Your discover filter still works.", icon: MapPin, invert: false },
  { key: 'read_receipts_enabled', title: 'Send read receipts', desc: 'Let matches see when you\'ve read their messages.', icon: MessageSquareText, invert: false },
];

const Toggle = ({ on, onClick, testId, disabled }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    data-testid={testId}
    className={`relative w-11 h-6 rounded-full transition-colors ${on ? 'bg-[#B91C1C]' : 'bg-black/[0.15]'} ${disabled ? 'opacity-40 cursor-not-allowed' : ''}`}
    aria-pressed={on}
  >
    <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${on ? 'translate-x-[22px]' : 'translate-x-0.5'}`} />
  </button>
);

export default function PrivacyVisibility() {
  const navigate = useNavigate();
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);

  useEffect(() => {
    api.get('/settings/visibility')
      .then((r) => setState(r.data))
      .catch(() => toast.error('Could not load privacy settings'))
      .finally(() => setLoading(false));
  }, []);

  const toggle = async (key) => {
    if (!state) return;
    const next = { ...state, [key]: !state[key] };
    setState(next); // optimistic
    setSaving(key);
    try {
      const r = await api.put('/settings/visibility', { [key]: next[key] });
      setState(r.data);
    } catch (e) {
      setState(state); // rollback
      toast.error('Save failed');
    } finally { setSaving(null); }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-white">
      <Loader2 className="w-6 h-6 animate-spin text-[#B91C1C]" />
    </div>
  );

  return (
    <div className="min-h-screen bg-white pb-16" data-testid="privacy-visibility-page">
      <header className="px-5 pt-6 pb-4 flex items-center gap-3">
        <button onClick={() => navigate(-1)} data-testid="privacy-back" aria-label="Back"
          className="w-9 h-9 rounded-full hover:bg-black/[0.04] flex items-center justify-center text-[#4B4B52]">
          <ArrowLeft size={18} />
        </button>
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[1.4px] text-[#8A8A93]">SETTINGS</p>
          <h1 className="font-brand text-[26px] font-[500] tracking-tight text-[#0F0F10] leading-tight">Privacy & Visibility</h1>
        </div>
      </header>

      <div className="max-w-xl mx-auto px-5 space-y-3">
        {ROWS.map((r) => {
          const Icon = r.icon;
          const on = !!state?.[r.key];
          return (
            <div key={r.key} className="flex items-center gap-3 py-4 px-4 rounded-2xl bg-white border border-[var(--hairline)]"
              data-testid={`visibility-${r.key.replace(/_/g, '-')}`}>
              <div className="w-10 h-10 rounded-full bg-black/[0.04] flex items-center justify-center flex-shrink-0">
                <Icon size={17} className="text-[#0F0F10]" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[14px] font-semibold text-[#0F0F10] leading-tight">{r.title}</p>
                <p className="text-[12px] text-[#8A8A93] mt-0.5 leading-snug">{r.desc}</p>
              </div>
              <Toggle
                on={on}
                onClick={() => toggle(r.key)}
                testId={`visibility-${r.key.replace(/_/g, '-')}-toggle`}
                disabled={saving === r.key}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}
